import {
    AssertionDescriptorUsageTypesEnum,
    CaseFileStaticItems,
    OrganizationTypesEnum,
    UserRightsEnum,
} from '../../utilities/Constants';
import { IQueryColumnDefinitionModel, IQueryFilterCriteria } from '../../interfaces/IQuery';
import React, { useEffect } from 'react';

import Authorization from '../../stores/Authorization';
import CaseHelper from '../../utilities/CaseHelper';
import CheckmateSelect from '../shared/CheckmateSelect';
import { IAssertionDescriptorModel } from '../../interfaces/IAssertionDescriptor';
import { IStaticItemLookupModel } from '../../interfaces/ILookup';
import Sort from '../../stores/Sort';
import UIHelper from '../../utilities/UIHelper';
import { useAuthContext } from '../../contexts/AuthContext';
import { useZoneContext } from '../../contexts/useZoneContext';
import { useZoneGetAssertionDescriptors } from '../../shared/react-query-hooks/useZoneGetAssertionDescriptors';
import { useZoneGetStaticItems } from '../../shared/react-query-hooks/useZoneGetStaticItems';

const QueryStaticItemFields = Object.assign({}, CaseFileStaticItems);

export function FieldFilter(props: {
    defaultFields?: IQueryColumnDefinitionModel[];
    label: string;
    labelClassNames: string;
    onChange: (selectedValues: IQueryFilterCriteria[]) => void;
    errors?: string[];
}) {
    const { user } = useAuthContext();
    const zoneContext = useZoneContext();

    const assertionDescriptorsApi = useZoneGetAssertionDescriptors(
        AssertionDescriptorUsageTypesEnum.Case
    );
    const staticItemsApi = useZoneGetStaticItems();

    const [availableFields, setAvailableFields] = React.useState<IQueryFilterCriteria[]>([]);
    const [selectedFields, setSelectedFields] = React.useState<IQueryFilterCriteria[]>([]);

    const { label, labelClassNames, errors } = props;

    const handleChange = (selectedItems: IQueryFilterCriteria[]) => {
        setSelectedFields(selectedItems);
        props.onChange(selectedItems);
    };

    const getStaticItemsList = (staticItems: IStaticItemLookupModel[]): IQueryFilterCriteria[] => {
        const packageDealAuthFilter = (staticItem: IStaticItemLookupModel) => {
            const isPackageDeal = staticItem.guid === QueryStaticItemFields.PackageSettlement.Guid;
            return (
                !isPackageDeal ||
                (isPackageDeal &&
                    Authorization.userHasRight(
                        UserRightsEnum.ViewPackageSettlement,
                        user,
                        zoneContext.zoneGuid
                    ))
            );
        };

        const staticSectionFilter = (staticItem: IStaticItemLookupModel) =>
            staticItem.id !== CaseFileStaticItems.DefenseCounselSection.Id;

        const staticItemToQueryFilterCriteriaMapper = (staticItem: IStaticItemLookupModel) => ({
            label: CaseHelper.getCaseFileStaticItemDisplayName(staticItems, staticItem.id),
            value: UIHelper.getName(staticItems, staticItem.id),
            isAssertionDescriptor: false,
            staticItemId: staticItem.id,
        });

        return staticItems
            .filter(packageDealAuthFilter)
            .filter(staticSectionFilter)
            .map(staticItemToQueryFilterCriteriaMapper);
    };

    React.useEffect(() => {
        if (assertionDescriptorsApi.isFetched && staticItemsApi.isFetched) {
            const assertionDescriptors = assertionDescriptorsApi.data ?? [];
            const staticItems = staticItemsApi.data ?? [];

            const assertionDescriptorFieldList: IQueryFilterCriteria[] = [];

            assertionDescriptors
                .filter(
                    (x) => x.usageType && x.usageType.id === AssertionDescriptorUsageTypesEnum.Case
                )
                .forEach((item: IAssertionDescriptorModel) => {
                    assertionDescriptorFieldList.push({
                        label: item.alternateDisplayName || item.displayName!,
                        value: item.name!,
                        isAssertionDescriptor: true,
                        assertionDescriptorGuid: item.guid,
                    });
                });

            const staticItemFieldList = getStaticItemsList(staticItems);
            const combined = assertionDescriptorFieldList.concat(staticItemFieldList);

            const excludedStaticItemIds = [
                QueryStaticItemFields.CaseStatusAsOfDate.Id,
                QueryStaticItemFields.DefenseCounselSection.Id,
                QueryStaticItemFields.DepositionSection.Id,
                QueryStaticItemFields.NotesSection.Id,
                QueryStaticItemFields.ExpertsSection.Id,
            ];

            const displayColumnFilter = (item: IQueryFilterCriteria) =>
                !excludedStaticItemIds.includes(item.staticItemId ?? -1);

            const isCompanyUser =
                user?.homeOrganization?.organizationType?.id ===
                OrganizationTypesEnum.Company.Value;
            const isLocalBasicUser = Authorization.isLocalScope(user, zoneContext.zoneGuid);
            const nationalCounselOnlyDisplayFilter = (item: IQueryFilterCriteria) =>
                item.staticItemId !== QueryStaticItemFields.SameCaseGroup.Id;

            const combinedFields =
                isCompanyUser || isLocalBasicUser
                    ? combined.filter(displayColumnFilter).filter(nationalCounselOnlyDisplayFilter)
                    : combined.filter(displayColumnFilter);

            combinedFields.sort(Sort.compareValues('label'));

            setAvailableFields(combinedFields);
        }
    }, [assertionDescriptorsApi.isFetched, staticItemsApi.isFetched]);

    React.useEffect(() => {
        if (!props.defaultFields) return;

        const displayColumnsSelectedDefault: IQueryFilterCriteria[] =
            props.defaultFields
                ?.map((c) =>
                    availableFields?.find(
                        (o) =>
                            (c.assertionDescriptorGuid &&
                                o.assertionDescriptorGuid?.toUpperCase() ===
                                    c.assertionDescriptorGuid.toUpperCase()) ||
                            (c.staticItemId && o.staticItemId === c.staticItemId)
                    )
                )
                .filter((v): v is IQueryFilterCriteria => Boolean(v)) ?? [];

        setSelectedFields(displayColumnsSelectedDefault);
    }, [availableFields, props.defaultFields]);

    useEffect(() => {
        props.onChange(selectedFields);
    }, [selectedFields, props.onChange]);

    return (
        <div>
            <div className="row" style={{ alignItems: 'center' }}>
                <div className={labelClassNames}>
                    <span>{label}</span>
                </div>
                <div className="col-xs-8 col-sm-9 col-lg-10">
                    <CheckmateSelect
                        isLoading={assertionDescriptorsApi.isLoading}
                        isMulti
                        onChange={handleChange}
                        options={availableFields}
                        value={selectedFields}
                    />
                </div>
            </div>
            {(errors?.length ?? 0 > 0) && (
                <div className="row" style={{ alignItems: 'center' }}>
                    <div className={labelClassNames}>&nbsp;</div>
                    <div className="text-danger col-xs-8 col-sm-9 col-lg-10">{errors}</div>
                </div>
            )}
        </div>
    );
}
